import React, { useState, useEffect } from "react";
import {
	Container,
	TextField,
	Button,
	Box,
	CircularProgress,
	Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { CSVLink } from "react-csv";
import { FaDownload } from "react-icons/fa";
import axios from "axios";

const errorCodeDescriptions = {
	"000": "Delivered",
	"0dc": "Absent Subscriber",
	206: "Absent Subscriber",
	"21b": "Absent Subscriber",
	"023": "Absent Subscriber",
	"027": "Absent Subscriber",
	"053": "Absent Subscriber",
	"054": "Absent Subscriber",
	"058": "Absent Subscriber",
	439: "Absent subscriber or ported subscriber or subscriber is barred",
	254: "Subscriber's phone inbox is full",
	220: "Subscriber's phone inbox is full",
	120: "Subscriber's phone inbox is full",
	"008": "Subscriber's phone inbox is full",
	255: "Invalid or inactive mobile number or subscriber's phone inbox is full",
	0: "Invalid or inactive mobile number or subscriber's phone inbox is full",
	"20b": "Invalid or inactive mobile number",
	"004": "Invalid or inactive mobile number",
	510: "Invalid or inactive mobile number",
	215: "Invalid or inactive mobile number",
	"20d": "Subscriber is barred on the network",
	130: "Subscriber is barred on the network",
	131: "Subscriber is barred on the network",
	222: "Network operator system failure",
	602: "Network operator system failure",
	306: "Network operator system failure",
	"032": "Network operator system failure or operator not supported",
	"085": "Subscriber is on DND",
	"065": "Message content or senderID is blocked on the promotional route",
	600: "Message content or senderID is blocked on the promotional route",
	"40a": "SenderID not whitelisted on the account",
	"082": "Network operator not supported",
	"00a": "SenderID is restricted by the operator",
	"078": "Restricted message content or senderID is blocked.",
	432: "Restricted message content or senderID is blocked.",
};

function isObject(obj) {
	let parsedObject = JSON.parse(obj);
	return (
		parsedObject !== undefined &&
		parsedObject !== null &&
		parsedObject.constructor == Object
	);
}

const getErrorCodeDescription = (report) => {
	try {
		// If the report is a string and matches an error code directly
		if (
			typeof report === "string" &&
			errorCodeDescriptions[report] !== undefined
		) {
			return `${report}: ${errorCodeDescriptions[report]}`;
		}
		// If the report is a JSON object, try to parse and extract the error code
		else if (isObject(report)) {
			const parsedReport = JSON.parse(report);
			const message = parsedReport.message || "";

			// Extract error code using regex
			const errorCodeMatch = message.match(/err:([0-9a-zA-Z]+)/);
			if (errorCodeMatch) {
				const errorCode = errorCodeMatch[1];
				const description = errorCodeDescriptions[errorCode];
				return description ? `${errorCode}: ${description}` : errorCode;
			}
		}

		return "Awaiting DLR"; // Default fallback if no conditions match
	} catch (e) {
		console.error("Error in getErrorCodeDescription:", e);
		return "Awaiting DLR";
	}
};

const formatPhone = (msisdn) =>
	msisdn.startsWith("234") ? "0" + msisdn.slice(3) : msisdn;

const columns = [
	{
		field: "msisdn",
		headerName: "Phone",
		width: 140,
		valueGetter: (params) => formatPhone(params),
	},
	{
		field: "text",
		headerName: "Message",
		flex: 1,
		minWidth: 200,
		renderCell: (params) =>
			params.value.length > 40
				? params.value.slice(0, 40) + "..."
				: params.value,
	},
	{
		field: "dlr_request",
		headerName: "Error Code",
		width: 200,
		headerAlign: "left",
		align: "left",
		valueGetter: (params) => getErrorCodeDescription(params),
	},
	{
		field: "dlr_status",
		headerName: "DLR Status",
		width: 120,
	},
	{
		field: "network",
		headerName: "Network",
		width: 100,
	},
	{
		field: "senderid",
		headerName: "Sender ID",
		width: 100,
	},
	{
		field: "created_at",
		headerName: "Date",
		width: 180,
		valueGetter: (params) =>
			new Date(params).toLocaleString("en-GB", {
				day: "2-digit",
				month: "short",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			}),
	},
];

const MessageSearch = () => {
	const [phoneNumber, setPhoneNumber] = useState("");
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(false);

	const today = new Date().toISOString().split("T")[0];

	useEffect(() => {
		// Load last 1000 messages on initial load
		const loadInitialMessages = async () => {
			setLoading(true);
			try {
				const token = localStorage.getItem("parallextoken");

				if (!token) {
					console.error("No authentication token found.");
					return;
				}

				const res = await axios.get(
					"https://parallexsms.approot.ng/get_last_1000_messages.php",
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				);

				if (res.data.success) {
					setResults(res.data.data);
				}
			} catch (err) {
				console.error("Failed to load messages", err);
			} finally {
				setLoading(false);
			}
		};

		loadInitialMessages();
	}, []);

	const handleSearch = async () => {
		if (!phoneNumber.trim()) return;

		setLoading(true);
		try {
			const token = localStorage.getItem("parallextoken");

			if (!token) {
				console.error("No authentication token found.");
				return;
			}

			const res = await axios.post(
				"https://parallexsms.approot.ng/search_messages.php",
				{ msisdn: phoneNumber },
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			console.log(res.data);
			if (res.data.success) {
				setResults(res.data.data);
			} else {
				setResults([]);
			}
		} catch (err) {
			console.error("Search failed", err);
		} finally {
			setLoading(false);
		}
	};

	const transformDataForCSV = (data) =>
		data.map((row) => ({
			msisdn: "'" + row.msisdn,
			network: row.network,
			senderid: row.senderid || "PARALLEX",
			created_at: row.created_at,
			error_code: row.dlr_request,
			description: getErrorCodeDescription(row.dlr_request),
			message: row.text,
			requestType: "SMS",
		}));

	return (
		<Container maxWidth='xl' sx={{ mt: 4 }}>
			<Typography
				variant='h4'
				gutterBottom
				sx={{ fontWeight: "bold", color: "#221891" }}
			>
				SMS Messages – Today
			</Typography>

			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					gap: 2,
					mb: 3,
					alignItems: "center",
				}}
			>
				<TextField
					label='Search by Phone Number'
					variant='outlined'
					size='small'
					value={phoneNumber}
					onChange={(e) => setPhoneNumber(e.target.value)}
				/>
				<Button
					variant='contained'
					onClick={handleSearch}
					disabled={loading}
					sx={{
						backgroundColor: "#DB9E27",
						"&:hover": { backgroundColor: "#c7981f" },
						color: "#fff",
						fontWeight: "bold",
					}}
				>
					{loading ? <CircularProgress size={20} color='inherit' /> : "Search"}
				</Button>

				{results.length > 0 && (
					<CSVLink
						data={transformDataForCSV(results)}
						filename={`SMS_${today}.csv`}
						className='flex items-center gap-2 px-4 py-2 bg-[#DB9E27] text-white rounded hover:bg-[#c7981f] transition'
					>
						<FaDownload />
						Export CSV
					</CSVLink>
				)}
			</Box>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
					<CircularProgress />
				</Box>
			) : (
				<Box sx={{ height: 600, width: "100%" }}>
					<DataGrid
						rows={results}
						columns={columns}
						getRowId={(row) => row.id}
						initialState={{
							pagination: { paginationModel: { pageSize: 10, page: 0 } },
						}}
						pageSizeOptions={[10, 25, 50, 100]}
						disableRowSelectionOnClick
						sx={{
							borderRadius: 2,
							boxShadow: 1,
							"& .MuiDataGrid-columnHeaders": {
								backgroundColor: "#221891",
								color: "#000",
							},
							"& .MuiDataGrid-row:hover": {
								backgroundColor: "#f9f9f9",
							},
						}}
					/>
				</Box>
			)}
		</Container>
	);
};

export default MessageSearch;
